import {
  afterRenderEffect,
  Component,
  computed,
  ElementRef,
  input,
  model,
  output,
  viewChild
} from '@angular/core';
import { FormaAttention, FormaAttentionVariant, FormaIcon } from 'forma';

/**
 * Pill-shaped free-text composer with a round send button, matching the Story
 * design. Enter sends, Shift+Enter inserts a newline. Disabled while the agent
 * is busy or when the field is empty.
 */
@Component({
  selector: 'app-agent-input-bar',
  imports: [FormaAttention, FormaIcon],
  host: { class: 'agent-input-bar' },
  styleUrl: './agent-input-bar.scss',
  templateUrl: './agent-input-bar.html',
})
export class AgentInputBar {
  readonly disabled = input<boolean>(false);
  /** Blocks sending only; the field stays editable (e.g. typing ahead while the agent answers). */
  readonly sendDisabled = input<boolean>(false);
  readonly placeholder = input<string>('');
  readonly ariaLabel = input<string>('Message');
  readonly sendLabel = input<string>('Send');
  readonly autoFocus = input(true);
  /** Plays a Forma attention border around the pill; null for none. */
  readonly attention = input<FormaAttentionVariant | null>(null);
  /** Keeps the attention border turning; switching it off fades it out. */
  readonly attentionLoop = input(false);
  readonly attentionEnd = output<void>();
  readonly value = model('');
  readonly send = output<string>();

  private readonly inputField = viewChild<ElementRef<HTMLTextAreaElement>>('input');
  // Starts true so the first afterRenderEffect run below also focuses the
  // field on initial mount, if it isn't disabled from the start.
  private wasDisabled = true;

  protected readonly canSend = computed(
    () => !this.disabled() && !this.sendDisabled() && this.value().trim().length > 0,
  );

  constructor() {
    // Re-focus the field once it becomes enabled again (e.g. after the agent
    // finishes responding), since the `disabled` attribute drops it from the
    // tab order and blurs it in the meantime. `afterRenderEffect` (rather than
    // a plain `effect`) is required here: it's guaranteed to run after the
    // DOM has been updated, so the `disabled` attribute is actually gone by
    // the time we call `.focus()` (a still-disabled element can't be focused).
    afterRenderEffect(() => {
      const isDisabled = this.disabled();
      if (this.autoFocus() && this.wasDisabled && !isDisabled) {
        this.inputField()?.nativeElement.focus();
      }
      this.wasDisabled = isDisabled;
    });
  }

  protected onInput(event: Event): void {
    this.value.set((event.target as HTMLTextAreaElement).value);
  }

  protected onEnter(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.shiftKey || keyboardEvent.isComposing) {
      return;
    }
    event.preventDefault();
    this.emit();
  }

  protected emit(): void {
    if (!this.canSend()) {
      return;
    }
    this.send.emit(this.value().trim());
    this.value.set('');
  }

  focus(): void {
    this.inputField()?.nativeElement.focus({ preventScroll: true });
  }
}
